<template>
  <section class="new-user-experience-modal" v-if="needsPasswordChange">
    <article class="new-user-experience-modal__content">
      <template v-if="!hasChangedPassword">
        <header class="new-user-experience-modal__header">
          <h2 class="new-user-experience-modal__title">
            Welcome, {{ user.first_name }}!
          </h2>
          <p>
            Welcome to ITR onDemand! We noticed you’re logging in as a new user.
            To keep your account as secure as possible, it’s best to change your
            password after first logging in. Please complete that step below,
            and then we’ll take you through our guided tour!
          </p>
        </header>
        <section>
          <PasswordComponent
            :horizontal_view="true"
            :show_cancel_button="false"
            :user_email="this.user.email"
            :show_current_password_field="true"
            @end-password-update-process="handlePasswordUpdateProcess()"
          />
        </section>
      </template>
      <template v-else>
        <header class="new-user-experience-modal__header">
          <button
            type="button"
            @click="closeModal()"
            class="new-user-experience-modal__close"
          >
            X
          </button>
          <h2
            class="new-user-experience-modal__title"
            v-html="componentContent.title"
          />
          <p v-html="componentContent.top_text" />
        </header>
        <section class="new-user-experience-modal__body">
          <div class="new-user-experience-modal__video-container">
            <iframe
              :src="`https://player.vimeo.com/video/${componentContent.vimeo_id}`"
              frameborder="0"
              webkitAllowFullScreen
              mozallowfullscreen
              allowFullScreen
              class="new-user-experience-modal__video"
            />
          </div>
        </section>
        <footer class="new-user-experience-modal__footer">
          <p v-html="componentContent.bottom_text" />
        </footer>
      </template>
    </article>
  </section>
</template>

<script>
  import axios from 'axios';
  import PasswordComponent from '~/components/user/PasswordComponent.vue';

  export default {
    name: 'NewUserExperienceModal',
    props: {
      user: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      hasChangedPassword: false,
      hasClosedModal: false,
      componentContent: {
        title: 'Let’s get started.',
        top_text:
          'Thank you for changing your password! Now we’re ready to take you on a tour of your <span class="bold">onDemand User Dashboard</span>.<br /><br />Watch the video below for an in-depth guide on how to navigate all the essentials, such as adding new users to your consulting reports, updating your billing information, or  managing your datasets.',
        vimeo_id: '611994727',
        bottom_text:
          'Rather watch later? You can find the tutorial video in the <a href="https://faq.itreconomics.com/en" class="new-user-experience-modal__link">Resources</a> section of your dashboard.',
      },
    }),
    components: {
      PasswordComponent,
    },
    async mounted() {
      if (!this.needsPasswordChange) return;

      const { data } = await axios.get('/new-user-experience');

      if (!data || data.data.length <= 0) return;

      this.componentContent = data.data[0];

      document.getElementById('site-header').classList.add('hide-z-index');
    },
    computed: {
      needsPasswordChange() {
        const needsPasswordChange =
          this.user.is_new_user && !this.hasClosedModal;

        if (needsPasswordChange) {
          document.body.classList.add('modal-is-open');
        } else {
          document.body.classList.remove('modal-is-open');
        }

        return needsPasswordChange;
      },
    },
    methods: {
      closeModal() {
        this.hasClosedModal = true;
        document.getElementById('site-header').classList.remove('hide-z-index');
      },
      handlePasswordUpdateProcess() {
        this.hasChangedPassword = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .new-user-experience-modal {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
    height: 100vh;

    padding: 25px;

    background-color: rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(1px);

    z-index: 1000;

    pointer-events: none;

    color: #183961;

    &__content {
      min-width: 200px;
      max-width: 864px;
      min-height: 200px;
      height: fit-content;
      max-height: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      padding: 45px;
      background-color: #f0f1f1;
      border-radius: 15px;

      pointer-events: all;
      user-select: text;
    }
    &__header {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      margin: 0 0 28px 0;
      background: inherit;
      background-color: #f0f1f1;
    }
    &__close {
      align-self: flex-end;

      width: 25px;
      height: 25px;
      border-radius: 50%;

      background-color: #183961;
      color: #ffffff;
    }
    &__title {
      margin: 0 0 28px 0;
      padding: 0;
    }
    &__body {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
    &__video-container {
      position: relative;
      width: 95%;
      height: 0;
      padding-top: 53.333%;
    }
    &__video {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    }
    &__footer {
      background-color: #f0f1f1;
      color: #183961;
      text-align: center;
      padding: 5px 10px;
      margin: 0;
      font-size: 17px;
    }
    a,
    a {
      font-weight: 700;
      text-decoration: underline;
      color: #183961 !important;

      &:hover {
        color: darken(#183961, 15%) !important;
      }
    }
  }
  :deep(.bold) {
    font-weight: 700;
  }
  :deep(a) {
    font-weight: 700;
    text-decoration: underline;
    color: #183961 !important;

    &:hover {
      color: darken(#183961, 15%) !important;
    }
  }
</style>
